import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./PatientVisits.scss";
import UsersImg from "../../assets/images/users.svg";
import User2Img from "../../assets/images/user-2.svg";
import User3Img from "../../assets/images/user-3.svg";
import User4Img from "../../assets/images/user-4.svg";
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import PrimarySideBar from "../../components/common/sideBar/primarySideBar";
import AddButtonSection from "../../components/common/AddButtonSection";
import DataGrid from '../../components/common/dataGrid/dataGrid';
import {
    convertToSearchField,
    generateColumnMetadata,
    orderObj,
    Feature,
} from "../../utils/utils";
import DataGridHeader from '../../components/common/dataGrid/dataGridAddPatientHeader';
import {
    addVisits,
    patientFormRender,
    PatientVisitsColumns,
} from "../../utils/serviceData";
import InputIterator from '../../components/common/inputIterator';
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import AddOrEditPatientVisit from "./addOrEditPatientVisit";
import DeletePatientVisit from "./DeletePatientVisit";
import DateSelect from '../../components/common/dateSelect';
import PatientSearchModal from "../../components/features/patients/PatientSearchModal";
import { showToast } from "../../components/common/toast";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { exportCSV, exportExcel, exportPdf } from "../../utils/exportUtils";
import { commonGetApi, commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import CapturePhoto from '../../components/common/capturePhoto/capturePhoto';
import moment from "moment";
import Util, { generateValidObject, dateFormat } from "../../utils/utils";
import { SORT_ORDER } from "../../constants/sortOrder";
import { useSelector } from "react-redux";


const SEARCH_FIELDS = ["patient_visit_code", "visit_date", "patient_name", "patient_code", "examiner_name", "purpose",];
const INITIAL_NAVIGATOR = {
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "patient_visit_code",
    sortDir: SORT_ORDER.ASC,
};

const CUSTOM_NON_REQUIRE_FIELDS = [
    "attented_by",
    "created_at",
    "updated_at",
    "created_by",
    "updated_by",
    "camp_details_id",
    "profile_picture",
    "first_name",
    "last_name",
    "ration_card_no",
    "health_insurance_card",
    "address",
    "district",
    "state",
    "city",
    "pincode",
    "remarks",
    "status",
];

const COLUMN_ORDER = [
    "patient_visit_code",
    "visit_date",
    "patient_name",
    "patient_code",
    "examiner_name",
    "purpose",
];

const COLUMN_LABELS = {
    patient_visit_code: "Visit Code",
    visit_date: "Visit Date",
    patient_name: "Patient Name",
    patient_code: "Patient ID",
    examiner_name: "Examiner",
    purpose: "Purpose",
};

// Maps DateSelect option value → PATIENT_VISIT_DETAILS filterType
const FILTER_TYPE_MAP = {
    1: "today",
    2: "week",
    3: "month",
    4: "custom",   // uses startDate + endDate
    5: "view_all",
};


const PatientVisits = () => {
    const camp_details_id = useSelector((state) => state.common.camp?.camp_details_id);
    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        watch,
    } = useForm({});

    const {
        control: patientControl,
        formState: { errors: patientErrors },
        handleSubmit: handlePatientSubmit,
        reset: resetPatient,
    } = useForm({ mode: "onChange", reValidateMode: "onChange" });

    const navigate = useNavigate();


    const [state, setState] = useState({
        columns: [],
        values: [],
        purposes: [],
        patientVisits: [],
        dashboard: [],
        formRender: addVisits,
        selectedPatientId: null,
        uploadedImg: null,
        loading: true,
        serverLoading: false,
        show: false,
        selectOption: 1,
    });

    const purposesRef = useRef([]);
    const navigatorRef = useRef(null);
    const filterRef = useRef({ filterType: 'today', startDate: null, endDate: null });

    const [visibility, setVisibility] = useState({
        sidebar: false,
        patientSearch: false,
        patientForm: false,
        editPatientVisit: false,
        deletePatientVisit: false,
    });

    const [selectedPatientVisit, setSelectedPatientVisit] = useState(null);

    const [navigator, setNavigator] = useState({
        ...INITIAL_NAVIGATOR,
        searchCriteria: convertToSearchField(SEARCH_FIELDS, ""),
    });


    const dashboardDetails = useCallback((name) => {
        const visit = state.dashboard?.find((item) => item?.name === name);
        return visit?.count || 0;
    }, [state.dashboard]);

    const selectedPurpose = watch("purpose");


    const init = useCallback(async (currentNavigator) => {
        try {
            setState(prev => ({ ...prev, loading: true }));

            // Read from ref — no stale closure, no dep on state.purposes
            let purposesList = purposesRef.current;
            if (purposesList.length === 0) {
                try {
                    const purposeData = await commonPostApi(API_ENDPOINTS.SEARCH_LOOKUP_DETAILS, {
                        lookup_type_name: "VISIT_PURPOSE",
                    });
                    purposesList = purposeData?.data?.data || [];
                    purposesRef.current = purposesList;
                    setState(prev => ({ ...prev, purposes: purposesList }));
                } catch (error) {
                    console.error("Error loading purposes:", error);
                }
            }

            // Use passed-in navigator; fall back to ref (never stale state)
            const nav = currentNavigator || navigatorRef.current;
            const { filterType, startDate, endDate } = filterRef.current;
            const payload = {
                query: {
                    page: nav?.page,
                    limit: nav?.rows,
                    filterType,
                    ...(filterType === 'custom' && { startDate, endDate }),
                    sortFields: [[nav?.sortBy, nav?.sortDir]],
                    searchCriteria: nav?.searchCriteria,
                },
            };

            const response = await commonPostApi(API_ENDPOINTS.PATIENT_VISIT_DETAILS, payload);

            const visitData = response?.data?.data || [];
            const totalRecords = response?.data?.total ?? response?.data?.totalRecords ?? 0;
            const currentPage = response?.data?.currentPage ?? nav?.page ?? 1;

            setNavigator(prev => ({
                ...prev,
                totalRecords: totalRecords,
                page: currentPage,
            }));

            const processedVisitData = visitData.map(visit => {
                const purposeName = purposesList.find(p => p.lookup_id === visit.purpose)?.name || visit.purpose;

                return {
                    ...visit,
                    visit_date: visit.visit_date ? moment(visit.visit_date, "DD-MM-YYYY").format("YYYY-MM-DD") : visit.visit_date,
                    purpose: purposeName,
                };
            });

            const columns = orderObj(
                COLUMN_ORDER,
                generateColumnMetadata({
                    data: processedVisitData,
                    nonRequireField: CUSTOM_NON_REQUIRE_FIELDS,
                    alias: COLUMN_LABELS,
                    defaultColumnsArr: PatientVisitsColumns,
                })
            );

            setState(prev => ({
                ...prev,
                columns,
                values: processedVisitData,
                loading: false,
            }));
        } catch (error) {
            console.error("Error initializing data:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_VISITS);
            setState(prev => ({ ...prev, loading: false }));
        }
    }, []);

    const processedVisitData = useCallback(
        (visit) => {
            const purposeName =
                state.purposes.find((p) => p.lookup_id === visit.purpose)?.name ||
                visit.purpose;

            return {
                ...visit,
                purpose: purposeName,
            };
        },
        [state.purposes]
    );

    const fetchAllPatientVisits = async () => {
        const exportObj = {
            query: {
                page: 1,
                limit: navigator.totalRecords,
                sortFields: [[navigator.sortBy, navigator.sortDir]],
                searchCriteria: navigator.searchCriteria,
            },

        }
        const res = await commonPostApi(API_ENDPOINTS.PATIENT_VISIT_DETAILS, exportObj)
        const data = res?.data?.data || [];
        return data.map(processedVisitData);
    }

    const loadPatientVisitDashboard = useCallback(async (startDate, endDate, selectOption) => {
        try {
            const filterType = FILTER_TYPE_MAP[selectOption] || "today";
            filterRef.current = { filterType, startDate, endDate };

            const visitPayload = {
                query: {
                    page: navigatorRef.current?.page || 1,
                    limit: navigatorRef.current?.rows || 10,
                    filterType,
                    ...(filterType === "custom" && { startDate, endDate }),
                    searchCriteria: navigatorRef.current?.searchCriteria || {},
                    sortFields: [[navigatorRef.current?.sortBy || "visit_date", navigatorRef.current?.sortDir || "ASC"]],
                },
            };

            let purposesList = purposesRef.current;
            if (purposesList.length === 0) {
                try {
                    const purposeData = await commonPostApi(API_ENDPOINTS.SEARCH_LOOKUP_DETAILS, {
                        lookup_type_name: "VISIT_PURPOSE",
                    });
                    purposesList = purposeData?.data?.data || [];
                    purposesRef.current = purposesList;
                    setState(prev => ({ ...prev, purposes: purposesList }));
                } catch (error) {
                    console.error("Error loading purposes:", error);
                }
            }

            const [dashboardRes, visitsRes] = await Promise.all([
                commonPostApi(API_ENDPOINTS.PATIENT_VISIT_DASHBOARD, {
                    filterType,
                    ...(filterType === "custom" && { startDate, endDate }),
                }),
                commonPostApi(API_ENDPOINTS.PATIENT_VISIT_DETAILS, visitPayload),
            ]);

            const dashboardData = dashboardRes?.data?.data || [];
            const visitData = visitsRes?.data?.data || [];
            const totalRecords = visitsRes?.data?.total ?? visitsRes?.data?.totalRecords ?? 0;
            const currentPage = visitsRes?.data?.currentPage ?? visitPayload.query.page;

            const processedVisits = visitData.map(visit => {
                const purposeName = purposesList.find(p => p.lookup_id === visit.purpose)?.name || visit.purpose;
                return {
                    ...visit,
                    visit_date: visit.visit_date ? moment(visit.visit_date, "DD-MM-YYYY").format("YYYY-MM-DD") : visit.visit_date,
                    purpose: purposeName,
                };
            });

            const columns = orderObj(
                COLUMN_ORDER,
                generateColumnMetadata({
                    data: processedVisits,
                    nonRequireField: CUSTOM_NON_REQUIRE_FIELDS,
                    alias: COLUMN_LABELS,
                    defaultColumnsArr: PatientVisitsColumns,
                })
            );

            setNavigator(prev => ({ ...prev, totalRecords, page: currentPage }));
            setState(prev => ({
                ...prev,
                dashboard: dashboardData,
                values: processedVisits,
                columns,
                loading: false,
            }));
        } catch (error) {
            console.error("Error loading dashboard:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_DASHBOARD);
        }
    }, []);

    const loadDropdownOptions = useCallback(async () => {
        try {
            const examinerData = await commonGetApi(API_ENDPOINTS.GET_EXAMINER_DETAILS);
            const examinersList = examinerData?.data?.data || [];

            const purposeData = await commonPostApi(API_ENDPOINTS.SEARCH_LOOKUP_DETAILS, {
                lookup_type_name: "VISIT_PURPOSE",
            });
            const purposesList = purposeData?.data?.data || [];

            purposesRef.current = purposesList;

            setState(prev => {
                const updatedFormRender = prev.formRender.map((field) => {
                    if (field.name === "examined_by") {
                        return {
                            ...field,
                            options: examinersList.map((examiner) => ({
                                label: examiner.user_name,
                                value: examiner.user_id,
                            })),
                        };
                    }
                    if (field.name === "purpose") {
                        return {
                            ...field,
                            options: purposesList.map((purpose) => ({
                                label: purpose.name,
                                value: purpose.lookup_id,
                            })),
                        };
                    }
                    return field;
                });
                const filteredFormRender = updatedFormRender.filter(
                    (field) => field.name !== "primary_visit_id"
                );
                return { ...prev, purposes: purposesList, formRender: filteredFormRender };
            });
        } catch (error) {
            console.error("Error loading dropdown options:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_FORM_OPTIONS);
        }
    }, []);


    const loadPatientVisits = useCallback(async (patientId) => {
        if (!patientId) return;

        try {
            const visitData = await commonPostApi(API_ENDPOINTS.GET_ALL_PATIENT_VISIT_DETAILS, {
                searchValue: "",
                patient_id: patientId,
            });
            const visits = visitData?.data?.data || [];
            setState(prev => ({ ...prev, patientVisits: visits }));
        } catch (error) {
            console.error("Error loading patient visits:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_LOAD_VISITS);
        }
    }, []);


    const onSubmit = useCallback(async (data) => {
        if (state.serverLoading) return;

        if (!state.selectedPatientId) {
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.SELECT_PATIENT_FIRST);
            return;
        }

        setState(prev => ({ ...prev, serverLoading: true }));

        try {
            const visitDate = dateFormat(data.date, "YYYY-MM-DD HH:mm:ss");
            const visitDetails = {
                patient_id: state.selectedPatientId,
                visit_date: visitDate,
                purpose: data.purpose,
                primary_visit_id: data.primary_visit_id || null,
                examined_by: data.examined_by,
            };

            await commonPostApi(API_ENDPOINTS.ADD_PATIENT_VISIT_DETAILS, visitDetails);
            showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.VISIT.ADDED);

            setVisibility(prev => ({ ...prev, sidebar: false }));
            init(navigator);
            console.log("init2");

            reset();
            setState(prev => ({ ...prev, selectedPatientId: null }));
        } catch (error) {
            console.error("Error adding visit:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_ADD_VISIT);
        } finally {
            setState(prev => ({ ...prev, serverLoading: false }));
        }
    }, [state.serverLoading, state.selectedPatientId, navigator, init, reset]);
    const onPatientSubmit = useCallback(async (data) => {
        if (state.serverLoading) return;
        setState((prev) => ({ ...prev, serverLoading: true }));
        try {
            let validObj = generateValidObject(data);
            const dob = dateFormat(validObj["dob"]);
            validObj = { ...validObj, dob };
            let finalData = validObj;
            let headers;
            if (state.uploadedImg && state.uploadedImg.size && state.uploadedImg.size > 0) {
                finalData = new FormData();
                finalData.append("profile_picture", state.uploadedImg);
                finalData.append("_payload", JSON.stringify(validObj));
                headers = { "Content-Type": "multipart/form-data" };
            } else {
                headers = { "Content-Type": "application/json" };
            }
            const res = await commonPostApi(API_ENDPOINTS.ADD_PATIENT_DETAILS, finalData, headers);

            if (res?.status === 201 || res?.data) {
                showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.PATIENT.ADDED);
                const data = res.data?.data ?? {};
                const fullName = `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim();
                setValue("patient_name", fullName);

                setState((prev) => ({
                    ...prev,
                    selectedPatientId: data?.patient_id ?? "NEW_PATIENT_ID",
                    uploadedImg: {},
                    serverLoading: false,
                }));
                setVisibility((prev) => ({ ...prev, patientForm: false }));
                resetPatient();
            } else {
                showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
                setState((prev) => ({ ...prev, serverLoading: false }));
            }
        } catch (error) {
            console.error("Error adding patient:", error);
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.FAILED_TO_ADD_PATIENT);
            setState((prev) => ({ ...prev, serverLoading: false }));
        }
    }, [state.serverLoading, state.uploadedImg, setValue, resetPatient]);


    const handleClick = useCallback((event) => {
        if (event === "addPatientVisit") {
            reset();
            setValue("purpose", "");
            setValue("primary_visit_id", "");
            setState(prev => ({
                ...prev,
                formRender: prev.formRender.filter(
                    field => field.name !== "primary_visit_id"
                )
            }));
            setVisibility(prev => ({ ...prev, sidebar: true }));
        }
    }, [reset, setValue]);

    const handleButtonClick = useCallback((action) => {
        switch (action) {
            case "searchPatients":
                setVisibility(prev => ({ ...prev, patientSearch: true }));
                break;
            case "addPatient":
                setVisibility(prev => ({ ...prev, patientForm: true }));
                break;
            default:
                break;
        }
    }, []);

    const handlePatientSelect = useCallback((selectedPatient) => {
        setValue("patient_name", selectedPatient.patient_name, {
            shouldValidate: true,
            shouldDirty: true,
        });
        clearErrors("patient_name");
        setState(prev => ({ ...prev, selectedPatientId: selectedPatient.patient_id }));
        loadPatientVisits(selectedPatient.patient_id);
        setVisibility(prev => ({ ...prev, patientSearch: false }));
    }, [setValue, loadPatientVisits, clearErrors]);

    const onImageCapture = useCallback((param) => {
        const capturedFile = Util.dataURLtoFile(
            param,
            `image-capture${moment().format("YYYYMMDDHHmmssa")}.jpg`
        );
        setState(prev => ({ ...prev, uploadedImg: capturedFile }));
    }, []);

    const handleSearchChange = useCallback((value) => {
        const newNavigator = {
            ...navigator,
            searchCriteria: convertToSearchField(SEARCH_FIELDS, value),
        };
        setNavigator(newNavigator);
        init(newNavigator);
    }, [navigator, init]);

    const onPageChange = useCallback((event) => {
        setState(prev => ({ ...prev, loading: true }));
        const newNavigator = {
            ...navigator,
            first: event?.first,
            page: event.page + 1,
        };
        setNavigator(newNavigator);
        init(newNavigator);
    }, [navigator, init]);

    const onRowCountChange = useCallback((e) => {
        setState(prev => ({ ...prev, loading: true }));
        const newNavigator = {
            ...navigator,
            rows: e?.target?.value,
            page: 1,
            first: 0,
        };
        setNavigator(newNavigator);
        init(newNavigator);
    }, [navigator, init]);

    const onSortClick = useCallback((event) => {
        setState(prev => ({ ...prev, loading: true }));
        const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
        const newNavigator = {
            ...navigator,
            sortBy: event?.sortField,
            sortDir: sortOrder,
            sortFields: [[event?.sortField, sortOrder]],
        };
        setNavigator(newNavigator);
        init(newNavigator);
    }, [navigator, init]);

    const onRowClick = useCallback((event) => {
        if (Feature?.featureVerify(Feature.listOfFeature.patientVisitView)) {
            navigate(`/patient/${event?.data?.patient_id}`);
        }
    }, [navigate]);



    const itemRenderer = useCallback((item, props) => (
        <div className="p-menuitem-content">
            {item.label === "Add visit" &&
                Feature?.featureVerify(Feature.listOfFeature.patientVisitAdd) && (
                    <Button
                        className="card-button-add"
                        icon="pi pi-plus"
                        label="Add Visit"
                        onClick={() => setVisibility(prev => ({ ...prev, sidebar: true }))}
                    />
                )}
            {item.label === "Edit" &&
                Feature?.featureVerify(Feature.listOfFeature.patientVisitEdit) && (
                    <Button
                        className="card-button-edit-secound"
                        label="Edit"
                        onClick={() => {
                            setSelectedPatientVisit(props?.props?.itemProp);
                            setVisibility(prev => ({ ...prev, editPatientVisit: true }));
                        }}
                    />
                )}
            {item.label === "Remove" &&
                Feature?.featureVerify(Feature.listOfFeature.patientVisitEdit) && (
                    <Button
                        className="card-button-delete"
                        label="Delete"
                        onClick={() => {
                            setSelectedPatientVisit(props?.props?.itemProp?.patient_visit_id);
                            setVisibility(prev => ({ ...prev, deletePatientVisit: true }));
                        }}
                    />
                )}
        </div>
    ), []);

    const buttons = useMemo(() => [
        {
            type: "primarySuccessButton",
            label: "Submit",
            class: "col-4",
            onClick: handleSubmit(onSubmit),
        },
        {
            type: "primaryCancelButton",
            label: "Discard",
            class: "col-4",
            onClick: () => {
                reset();
                clearErrors();
                setState(prev => ({ ...prev, selectedPatientId: null }));
                setVisibility(prev => ({ ...prev, sidebar: false }));
            },
        },
    ], [handleSubmit, onSubmit, reset, clearErrors]);

    const patientButtons = useMemo(() => [
        {
            spType: "file",
            type: "primaryButton",
            label: "Upload Profile Picture",
            icon: "pi pi-upload",
            class: "col-9",
            info: state.uploadedImg ? state.uploadedImg.name : "",
            onClick: (e) => {
                const file = e?.target?.files?.[0];
                if (file) {
                    setState(prev => ({
                        ...prev,
                        uploadedImg: file
                    }));
                }
                e.target.value = null;
            },
        },
        {
            type: "secondaryButton",
            label: "Reset",
            class: "col-3",
            onClick: () => {
                setState(prev => ({ ...prev, uploadedImg: null }));
            }
        },
        {
            type: "render",
            render: (
                <div className="msgText font-normal text-sm p-2 pb-3">
                    Allowed JPG, GIF or PNG. Max size of 800KB
                </div>
            ),
        },
        {
            type: "render",
            render: (
                <CapturePhoto
                    imgWidth={200}
                    imgHeight={150}
                    webcamWidth={410}
                    webcamHeight={320}
                    onImageCapture={onImageCapture}
                />
            ),
        },
        {
            type: "primarySuccessButton",
            label: "Submit",
            class: "col-4",
            onClick: handlePatientSubmit(onPatientSubmit),
        },
        {
            type: "primaryCancelButton",
            label: "Cancel",
            class: "col-4",
            onClick: () => {
                setVisibility(prev => ({ ...prev, patientForm: false }));
                resetPatient();
                setState(prev => ({ ...prev, uploadedImg: {} }));
            },
        },
    ], [state.uploadedImg?.name, onImageCapture, handlePatientSubmit, onPatientSubmit, resetPatient]);

    useEffect(() => {
        navigatorRef.current = navigator;
    }, [navigator]);
    useEffect(() => {
        loadDropdownOptions()   ;
    }, []);

    useEffect(() => {
        if (selectedPurpose && state.purposes.length > 0) {
            const purposeName = state.purposes.find(p => p.lookup_id === selectedPurpose)?.name;
            const initialVisitId = state.purposes.find(p => p.name === "Initial Visit")?.lookup_id;
            if (purposeName && purposeName !== "Initial Visit") {
                const initialVisits = state.patientVisits
                    .filter(visit => visit.purpose === initialVisitId);
                if (initialVisits.length === 0) {
                    showToast(
                        TOAST_TYPE.ERROR,
                        "Please create Initial Visit first"
                    );
                    setState(prev => ({
                        ...prev,
                        formRender: prev.formRender.filter(
                            field => field.name !== "primary_visit_id"
                        ),
                    }));

                    setValue("primary_visit_id", "");
                    return;
                }
                const primaryVisitField = {
                    type: "dropdown",
                    name: "primary_visit_id",
                    label: "Primary Visit ID",
                    fieldLabel: "primary_visit_id",
                    options: initialVisits.map(visit => ({
                        label: visit.patient_visit_code,
                        value: visit.patient_visit_id,
                    })),
                    rules: {
                        required: "Primary visit ID is required",
                    },
                };
                setState(prev => {
                    const existingFieldIndex = prev.formRender.findIndex(
                        field => field.name === "primary_visit_id"
                    );
                    if (existingFieldIndex !== -1) {
                        const newFormRender = [...prev.formRender];
                        newFormRender[existingFieldIndex] = primaryVisitField;
                        return { ...prev, formRender: newFormRender };
                    }
                    const purposeIndex = prev.formRender.findIndex(
                        field => field.name === "purpose"
                    );

                    const newFormRender = [...prev.formRender];
                    newFormRender.splice(purposeIndex + 1, 0, primaryVisitField);

                    return { ...prev, formRender: newFormRender };
                });
            }
            else {
                setState(prev => ({
                    ...prev,
                    formRender: prev.formRender.filter(
                        field => field.name !== "primary_visit_id"
                    ),
                }));
                setValue("primary_visit_id", "");
            }
        }

    }, [selectedPurpose, state.purposes, state.patientVisits, setValue]);
    return (
        <div className="PatientVisits__main-container">
            <div className="mt-4 mb-4">
                <DateSelect
                    show={state.show}
                    setShow={(show) => setState(prev => ({ ...prev, show }))}
                    onDateRangeChange={loadPatientVisitDashboard}
                    selectOption={state.selectOption}
                    setSelectOption={(updater) =>
                        setState((prev) => ({
                            ...prev,
                            selectOption:
                                typeof updater === "function"
                                    ? updater(prev.selectOption)
                                    : updater,
                        }))
                    }
                    onViewAll={() => loadPatientVisitDashboard(null, null, 5)}
                />
                <div className="Patients__card-container">
                    <ul className="Patients__card-holder">
                        <li>
                            <div className="card-left-sec">
                                <p className="card-title">Total Visits</p>
                                <p className="card-info">
                                    <span className="total-count">
                                        {dashboardDetails("Visits")}
                                    </span>
                                </p>
                            </div>
                            <div className="card-right-sec">
                                <span className="card-icon bg-one">
                                    <img src={UsersImg} alt="Total Visits" />
                                </span>
                            </div>
                        </li>

                        <li>
                            <div className="card-left-sec">
                                <p className="card-title">1st (New)</p>
                                <p className="card-info">
                                    <span className="total-count">
                                        {dashboardDetails("1st (New)")}
                                    </span>
                                </p>
                            </div>
                            <div className="card-right-sec">
                                <span className="card-icon bg-two">
                                    <img src={User2Img} alt="New Visits" />
                                </span>
                            </div>
                        </li>

                        <li>
                            <div className="card-left-sec">
                                <p className="card-title">7th (Return)</p>
                                <p className="card-info">
                                    <span className="total-count">
                                        {dashboardDetails("7th (Return)")}
                                    </span>
                                </p>
                            </div>
                            <div className="card-right-sec">
                                <span className="card-icon bg-three">
                                    <img src={User3Img} alt="Follow-up Visits" />
                                </span>
                            </div>
                        </li>

                        <li>
                            <div className="card-left-sec">
                                <p className="card-title">14th (Return)</p>
                                <p className="card-info">
                                    <span className="total-count">
                                        {dashboardDetails("14th (Return)")}
                                    </span>
                                </p>
                            </div>
                            <div className="card-right-sec">
                                <span className="card-icon bg-four">
                                    <img src={User4Img} alt="Completed Visits" />
                                </span>
                            </div>
                        </li>

                        <li>
                            <div className="card-left-sec">
                                <p className="card-title">40th (Return)</p>
                                <p className="card-info">
                                    <span className="total-count">
                                        {dashboardDetails("40th (Return)")}
                                    </span>
                                </p>
                            </div>
                            <div className="card-right-sec">
                                <span className="card-icon bg-four">
                                    <img src={User4Img} alt="Missed Visits" />
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <DataGrid
                header={
                    <DataGridHeader
                        handleClick={handleClick}
                        isSearchEnabled={true}
                        isDownloadButton={true}
                        isCalenderEnabled={false}
                        isButtonEnabledLeft={false}
                        exportCSV={async () => {
                            const allData = await fetchAllPatientVisits();
                            exportCSV(allData, state.columns, "Patient_Visits");
                        }}
                        exportExcel={async () => {
                            const allData = await fetchAllPatientVisits();
                            exportExcel(allData, state.columns, "Patient_Visits");
                        }}
                        exportPdf={async () => {
                            const allData = await fetchAllPatientVisits();
                            exportPdf(allData, state.columns, "Patient_Visits");
                        }}
                        isButtonEnabledRight={Feature?.featureVerify(
                            Feature.listOfFeature.patientVisitAdd
                        )}
                        searchPlaceHolder="Search Patient Visits"
                        currentPageHeader="Patient Visits List"
                        onChange={handleSearchChange}
                    />
                }
                onSortClick={onSortClick}
                onRowCountChange={onRowCountChange}
                onRowClick={onRowClick}
                onPageChange={onPageChange}
                sortable={true}
                navigator={navigator}
                column={state.columns}
                value={state.values}
                loading={state.loading}
                optionsView={
                    (Feature?.featureVerify(Feature.listOfFeature.patientVisitAdd) ||
                        Feature?.featureVerify(Feature.listOfFeature.patientVisitEdit)) && [
                        {
                            items: [
                                {
                                    label: "Edit",
                                    icon: "pi pi-upload",
                                    template: itemRenderer,
                                },
                                {
                                    label: "Remove",
                                    icon: "pi pi-upload",
                                    template: itemRenderer,
                                },
                            ],
                        },
                    ]
                }
                isAccordionEnabled={false}
                isPaginatorEnabled={true}
            />

            <PrimarySideBar
                visible={visibility.sidebar}
                onHide={() => {
                    reset();
                    setValue("purpose", "");
                    setValue("primary_visit_id", "");
                    setState(prev => ({
                        ...prev,
                        formRender: prev.formRender.filter(
                            field => field.name !== "primary_visit_id"
                        )
                    }));
                    setVisibility(prev => ({ ...prev, sidebar: false }));
                }}
                header={<div className="sideBar-header">Add visits</div>}
            >
                <InputIterator
                    control={control}
                    errors={errors}
                    formRender={state.formRender}
                    onClick={handleButtonClick}
                />
                <div className="pt-3">
                    <AddButtonSection disabled={state.serverLoading} buttons={buttons} />
                </div>
            </PrimarySideBar>

            <DevTool control={control} />

            {visibility.deletePatientVisit && (
                <DeletePatientVisit
                    visible={visibility.deletePatientVisit}
                    patientVisit={selectedPatientVisit}
                    setVisible={(bool, final) => {
                        setVisibility(prev => ({ ...prev, deletePatientVisit: bool }));
                        if (final) {
                            setState(prev => ({ ...prev, loading: true }));
                            init(navigator);
                        }
                    }}
                />
            )}

            {visibility.editPatientVisit && (
                <AddOrEditPatientVisit
                    patientVisit={selectedPatientVisit}
                    patientId={selectedPatientVisit?.patient_id}
                    mode="edit"
                    visible={visibility.editPatientVisit}
                    setVisible={(bool, refresh) => {
                        setVisibility(prev => ({ ...prev, editPatientVisit: bool }));
                        if (refresh) init(navigator);
                    }}
                />
            )}

            <PatientSearchModal
                visible={visibility.patientSearch}
                onHide={() => setVisibility(prev => ({ ...prev, patientSearch: false }))}
                onPatientSelect={handlePatientSelect}
            />

            <PrimarySideBar
                visible={visibility.patientForm}
                onHide={() => {
                    setVisibility(prev => ({ ...prev, patientForm: false }));
                    resetPatient();
                    setState(prev => ({ ...prev, uploadedImg: {} }));
                }}
                header={<div className="sideBar-header">Add Patient</div>}
            >
                <InputIterator
                    control={patientControl}
                    errors={patientErrors}
                    formRender={patientFormRender}
                />
                <div className="pt-3">
                    <AddButtonSection disabled={state.serverLoading} buttons={patientButtons} />
                </div>
            </PrimarySideBar>
        </div>
    );
};

export default PatientVisits;